import { FunctionComponent, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import Overlay from '@/components/molecules/overlay';
import { zIndex } from '@/constants/zIndex';
import { MODAL_BUTTON_TYPE, ModalButtonProps } from '@/types/modal';

export interface AlertModalProps {
  index: number;
  onClickOverlay?: () => void;
  title: ReactNode;
  buttons: Array<ModalButtonProps>;
  body?: ReactNode;
  message?: ReactNode;
  centered?: boolean;
}

const AlertModal: FunctionComponent<AlertModalProps> = ({
  title,
  message,
  body,
  buttons,
  onClickOverlay,
  centered,
}) => {
  return (
    <Overlay index={zIndex.MobileWebHeader} onClickOverlay={onClickOverlay}>
      <AlertModalContainer>
        <AlertModalContent centered={centered}>
          <AlertModalTitle>{title}</AlertModalTitle>
          {message && <AlertModalMessage centered={centered}>{message}</AlertModalMessage>}
          {body && <>{body}</>}
        </AlertModalContent>
        <AlertModalButtonWrapper>
          {buttons.map((button, index) => {
            const { type, label, labelColor, onClick } = button;
            return (
              <AlertModalButton
                key={`alertModalButton-${index}`}
                $type={type || MODAL_BUTTON_TYPE.DEFAULT}
                labelColor={labelColor}
                onClick={onClick}
              >
                {label || '취소'}
              </AlertModalButton>
            );
          })}
        </AlertModalButtonWrapper>
      </AlertModalContainer>
    </Overlay>
  );
};

export default AlertModal;

export const AlertModalContainer = styled.div`
  width: 296px;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 16px 0 rgb(0 0 0 / 8%);

  ${({ theme }) => `
    background-color: ${theme.color.white};
  `}
`;

export const AlertModalContent = styled.div<{ centered?: boolean }>`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  ${({ centered }) =>
    centered &&
    `
    justify-content: center;
    align-items: center;
  `}
`;

export const AlertModalTitle = styled.h3`
  ${({ theme }) => `
    ${theme.typography.UI20SB};
    color: ${theme.color.gray800};
    `};
`;

export const AlertModalMessage = styled.div<{ centered?: boolean }>`
  margin-top: 8px;

  ${({ theme }) => `
    ${theme.typography.UI15R};
    color: ${theme.color.gray600};
    `};
  white-space: pre-wrap;
  ${({ centered }) =>
    centered &&
    `
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align:center;
  `}
`;

export const AlertModalButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  button + button {
    margin-left: 8px;
  }
`;

const DefaultModalButton = css`
  ${({ theme }) => `
    ${theme.typography.UI17SB};
    color: ${theme.color.gray600};
    background: ${theme.color.white};
    border: 1px solid ${theme.color.gray200};
    
    &:active {
      background: ${theme.color.gray100};
    };
  `};
`;

const BoldModalButton = css`
  ${({ theme }) => `
    color: ${theme.color.white};
    background: ${theme.color.gray700};
    
    &:active {
      background: ${theme.color.gray800};
    };
  `};
`;

const PrimaryModalButton = css`
  ${({ theme }) => `
    color: ${theme.color.white};
    background: ${theme.color.primary600};

    &:active {
      background: ${theme.color.primary800};
    };
  `};
`;

const DangerModalButton = css`
  ${({ theme }) => `
    color: ${theme.color.white};
    background: ${theme.color.red500};

    &:active {
      background: ${theme.color.red700};
    };
  `};
`;

export const AlertModalButton = styled(DefaultButtonStyled)<{
  $type?: MODAL_BUTTON_TYPE;
  labelColor?: string;
}>`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 8px;

  ${({ theme }) => `
    ${theme.typography.UI17SB};
    `};

  ${({ $type }) => {
    switch ($type) {
      case MODAL_BUTTON_TYPE.DEFAULT:
        return DefaultModalButton;
      case MODAL_BUTTON_TYPE.BOLD:
        return BoldModalButton;
      case MODAL_BUTTON_TYPE.PRIMARY:
        return PrimaryModalButton;
      case MODAL_BUTTON_TYPE.DANGER:
        return DangerModalButton;
    }
  }};
`;
